<template>
  <MediaIframe
    v-if="type === 'iFrame'"
    :media="media"
    v-bind="
      ContentfulLivePreview.getProps({
        entryId: item.sys.id,
        fieldId: 'internalName'
      })
    "
  />

  <div
    v-else-if="type === 'iFrame with Script-Tag'"
    :class="[
      'html-block',
      positioningClassNames,
      fullWidth && $style.fullWidth
    ]"
    v-bind="
      ContentfulLivePreview.getProps({
        entryId: item.sys.id,
        fieldId: 'internalName'
      })
    "
  >
    <div class="html-block__inner">
      <iframe
        :id="iframeId"
        ref="iframeRef"
        width="100%"
        height="300"
        :src="iframeUrl"
        frameborder="0"
        scrolling="no"
      />
    </div>
  </div>
  <HtmlBlock
    v-else
    :content="item.fields.html ?? ''"
    :cols-centered="true"
    :content-centered="false"
    :cols="cols"
    :class="fullWidth && $style.fullWidth"
    v-bind="
      ContentfulLivePreview.getProps({
        entryId: item.sys.id,
        fieldId: 'internalName'
      })
    "
  />
</template>

<script setup lang="ts">
import { HtmlBlock } from '@hypercodestudio/basler-components';
import { MediaIframe } from '@hypercodestudio/basler-components';
import { iframeResizer } from 'iframe-resizer';
import type { ICustomHtml, ICustomHtmlFields } from '~/lib/ContentfulService';
import { mapColsWidth } from '~/utils/mapColsWidth';
import { useIframeResizer } from '~/composables/useIframeResizer';
import { ContentfulLivePreview } from '@contentful/live-preview';
import { convertContentfulToWebappLocaleCode } from '~/utils/convertContentfulToWebappLocaleCode';

interface Props {
  item: ICustomHtml;
}

const props = defineProps<Props>();
const locale = useLocale();

const fullWidth = computed(() => props.item.fields.htmlWidth === 'full width');
const cols = computed(() =>
  fullWidth.value
    ? 12
    : mapColsWidth(
        props.item.fields.htmlWidth as Exclude<
          ICustomHtmlFields['htmlWidth'],
          'full width'
        >
      ) || 6
);
const iframeUrl = computed(
  () =>
    `/${convertContentfulToWebappLocaleCode(locale.value)}/_technical/html/${
      props.item.sys.id
    }`
);

const iframeId = computed(() => `${props.item.sys.id}-iframe`);
const useIframe = computed(() => props.item.fields.embedAsIframe ?? false);
const type = computed(() => props.item.fields.type ?? 'Iframe');
const iframeRef = ref<HTMLIFrameElement>();

const media = computed(() => {
  return {
    url: props.item.fields.iFrameUrl,
    caption: 'Iframe with external content',
    aspectRatio: props.item.fields.aspectRatio ?? '4:3',
    html: props.item.fields.html,
    width: props.item.fields.iFrameWidth ?? -1,
    height: props.item.fields.iFrameHeight ?? -1,
    customArgs: {}
  };
});

// XXX: import from hypercode?
function useBlockPositioning(
  className: string,
  cols: number,
  colsCentered: boolean,
  contentCentered: boolean
) {
  // For styles see src/assets/styles/abstracts/mixins/_block-positioning.scss
  const positioningClassNames = computed(() => ({
    [`${className}--max-cols-${cols}`]: true,
    [`${className}--cols-even`]: cols % 2 === 0,
    [`${className}--cols-odd`]: cols % 2 !== 0,
    [`${className}--cols-centered`]: colsCentered,
    [`${className}--content-centered`]: contentCentered
  }));

  return {
    positioningClassNames
  };
}

const { positioningClassNames } = useBlockPositioning(
  'html-block',
  cols.value,
  true,
  false
);

onMounted(() => {
  if (props.item.fields.iframeResizerIframeId) {
    iframeResizer(
      {
        // XXX: might need an additional origin check?
        checkOrigin: false
      },
      `#${props.item.fields.iframeResizerIframeId}`
    );
  }
});

if (useIframe.value) {
  useHead({
    link: [
      {
        rel: 'preload',
        href: () => iframeUrl.value,
        as: 'document'
      }
    ]
  });

  useIframeResizer(iframeRef);
}
</script>

<style module>
.fullWidth {
  max-width: none !important;
}
</style>
